import axios from 'axios';

class WhatsAppService {
  constructor() {
    this.baseURL = process.env.REACT_APP_ULTRAMSG_BASE_URL || 'https://api.ultramsg.com';
    this.instanceId = process.env.REACT_APP_ULTRAMSG_INSTANCE_ID;
    this.token = process.env.REACT_APP_ULTRAMSG_TOKEN;
  }

  getHeaders() {
    return {
      'Content-Type': 'application/x-www-form-urlencoded'
    };
  }

  formatPhoneNumber(phone) {
    let cleanPhone = phone.replace(/[^\d]/g, '');
    
    if (cleanPhone.startsWith('0')) {
      cleanPhone = '260' + cleanPhone.substring(1);
    } else if (!cleanPhone.startsWith('260')) {
      cleanPhone = '260' + cleanPhone;
    }
    
    return cleanPhone + '@c.us';
  }

  async sendTextMessage(to, message) {
    try {
      const formattedPhone = this.formatPhoneNumber(to);
      const url = `${this.baseURL}/${this.instanceId}/messages/chat`;
      
      const data = new URLSearchParams({
        token: this.token,
        to: formattedPhone,
        body: message,
        priority: '1'
      });

      const response = await axios.post(url, data, {
        headers: this.getHeaders()
      });

      return {
        success: true,
        data: response.data,
        messageId: response.data?.id
      };
    } catch (error) {
      console.error('WhatsApp send error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        details: error.response?.data
      };
    }
  }

  async sendBulkMessages(recipients, message, onProgress = null) {
    const results = [];
    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < recipients.length; i++) {
      const recipient = recipients[i];
      const phoneNumber = recipient.phone || recipient.phoneNumber || recipient;
      
      try {
        const result = await this.sendTextMessage(phoneNumber, message);
        
        if (result.success) {
          successCount++;
          results.push({
            recipient: phoneNumber,
            status: 'sent',
            messageId: result.messageId,
            data: result.data
          });
        } else {
          failedCount++;
          results.push({
            recipient: phoneNumber,
            status: 'failed',
            error: result.error
          });
        }
      } catch (error) {
        failedCount++;
        results.push({
          recipient: phoneNumber,
          status: 'failed',
          error: error.message
        });
      }

      if (onProgress) {
        onProgress({
          current: i + 1,
          total: recipients.length,
          successCount,
          failedCount,
          progress: ((i + 1) / recipients.length) * 100
        });
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    return {
      success: true,
      summary: {
        total: recipients.length,
        sent: successCount,
        failed: failedCount
      },
      results
    };
  }

  async sendImageMessage(to, imageUrl, caption = '') {
    try {
      const formattedPhone = this.formatPhoneNumber(to);
      const url = `${this.baseURL}/${this.instanceId}/messages/image`;
      
      const data = new URLSearchParams({
        token: this.token,
        to: formattedPhone,
        image: imageUrl,
        caption: caption,
        priority: '1'
      });

      const response = await axios.post(url, data, {
        headers: this.getHeaders()
      });

      return {
        success: true,
        data: response.data,
        messageId: response.data?.id
      };
    } catch (error) {
      console.error('WhatsApp image send error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  async sendDocumentMessage(to, documentUrl, filename, caption = '') {
    try {
      const formattedPhone = this.formatPhoneNumber(to);
      const url = `${this.baseURL}/${this.instanceId}/messages/document`;
      
      const data = new URLSearchParams({
        token: this.token,
        to: formattedPhone,
        document: documentUrl,
        filename: filename,
        caption: caption,
        priority: '1'
      });

      const response = await axios.post(url, data, {
        headers: this.getHeaders()
      });

      return {
        success: true,
        data: response.data,
        messageId: response.data?.id
      };
    } catch (error) {
      console.error('WhatsApp document send error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  async getInstanceStatus() {
    try {
      const url = `${this.baseURL}/${this.instanceId}/instance/status`;
      const params = { token: this.token };
      
      const response = await axios.get(url, { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('WhatsApp status check error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  async getQRCode() {
    try {
      const url = `${this.baseURL}/${this.instanceId}/instance/qr`;
      const params = { token: this.token };
      
      const response = await axios.get(url, { params });
      
      return {
        success: true,
        qrCode: response.data
      };
    } catch (error) {
      console.error('WhatsApp QR code error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  validateConfiguration() {
    const errors = [];
    
    if (!this.instanceId) {
      errors.push('REACT_APP_ULTRAMSG_INSTANCE_ID is required');
    }
    
    if (!this.token) {
      errors.push('REACT_APP_ULTRAMSG_TOKEN is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

const whatsappService = new WhatsAppService();
export default whatsappService;